import { t } from 'i18next';
import * as yup from 'yup';

export const DEFAULT_PALETTE_COLOR = '#6B7280';

// Muted palette — WCAG AA compliant (≥4.5:1 contrast with dark text #1F2937)
export const DEFAULT_COLOR_PALETTE = [
  '#E0ACAC',
  '#E0C4A0',
  '#DDD4A0',
  '#D6D4A0',
  '#BDD4A8',
  '#A8D4B8',
  '#A0D1CC',
  '#A0C8D6',
  '#A7C0DC',
  '#B0B5D6',
  '#C0B0D6',
  '#D8AECA',
] as const;

export const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const DARK_TEXT = '#1F2937';
const LIGHT_TEXT = '#FFFFFF';

export const getLuminance = (hexColor: string): number => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const linearize = (channel: number): number =>
    channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);

  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
};

// WCAG 2.0 contrast ratio: https://www.w3.org/TR/WCAG20/#contrast-ratiodef
export const getContrastRatio = (hex1: string, hex2: string): number => {
  const l1 = getLuminance(hex1);
  const l2 = getLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Pick whichever text color (dark or white) has better WCAG contrast
 * ratio against the given background.
 */
export const getContrastTextColor = (bgColor: string): string => {
  const darkContrast = getContrastRatio(bgColor, DARK_TEXT);
  const lightContrast = getContrastRatio(bgColor, LIGHT_TEXT);
  return darkContrast > lightContrast ? DARK_TEXT : LIGHT_TEXT;
};

export const paletteColorStyles = (bgColor: string) => ({
  backgroundColor: bgColor,
  color: getContrastTextColor(bgColor),
});

export const hexColorYupField = () =>
  yup
    .string()
    .matches(HEX_COLOR_REGEX, t('form_validation_errors.invalid_color_format'))
    .required(t('form_validation_errors.required_simple'));
